import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/components/ui/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card } from "@/components/ui/card"
import { AvatarUpload } from "@/components/settings/AvatarUpload"

interface ChatInterfaceSettingsProps {
  chatbotId: string | null;
}

export function ChatInterfaceSettings({ chatbotId }: ChatInterfaceSettingsProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [themeColor, setThemeColor] = useState("#007AFF")
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF")
  const [textColor, setTextColor] = useState("#000000")
  const [borderRadius, setBorderRadius] = useState("8px")
  const [chatHeight, setChatHeight] = useState("600px")
  const [chatWidth, setChatWidth] = useState("100%")
  const [fontFamily, setFontFamily] = useState("Inter")

  const { data: chatbot, isLoading } = useQuery({
    queryKey: ['chatbot', chatbotId],
    queryFn: async () => {
      if (!chatbotId) return null
      
      const { data, error } = await supabase
        .from('chatbots')
        .select('*')
        .eq('id', chatbotId)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!chatbotId
  })

  useEffect(() => {
    if (chatbot) {
      setThemeColor(chatbot.theme_color || "#007AFF")
      setBackgroundColor(chatbot.background_color || "#FFFFFF")
      setTextColor(chatbot.text_color || "#000000")
      setBorderRadius(chatbot.border_radius || "8px")
      setChatHeight(chatbot.chat_height || "600px")
      setChatWidth(chatbot.chat_width || "100%")
      setFontFamily(chatbot.font_family || "Inter")
    }
  }, [chatbot])

  const { mutate: updateChatbot, isPending } = useMutation({
    mutationFn: async () => {
      if (!chatbotId) throw new Error("No chatbot selected")

      const { error } = await supabase
        .from('chatbots')
        .update({
          theme_color: themeColor,
          background_color: backgroundColor,
          text_color: textColor,
          border_radius: borderRadius,
          chat_height: chatHeight,
          chat_width: chatWidth,
          font_family: fontFamily
        })
        .eq('id', chatbotId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot', chatbotId] })
      toast({
        title: "Settings saved",
        description: "Your widget style settings have been updated successfully."
      })
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update settings"
      })
    }
  })

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!chatbotId) {
    return <div>Please select a chatbot to configure</div>
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <div className="space-y-6">
          <div className="space-y-4">
            <Label className="text-lg font-semibold">Chat Bot Avatar</Label>
            <AvatarUpload />
          </div>

          <div className="space-y-4">
            <Label>Theme Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={themeColor}
                onChange={(e) => setThemeColor(e.target.value)}
                className="w-20 h-10"
              />
              <Input
                type="text"
                value={themeColor}
                onChange={(e) => setThemeColor(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-4">
            <Label>Background Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="w-20 h-10"
              />
              <Input
                type="text"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-4">
            <Label>Text Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="w-20 h-10"
              />
              <Input
                type="text"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-4">
            <Label>Border Radius</Label>
            <Select value={borderRadius} onValueChange={setBorderRadius}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0px">None</SelectItem>
                <SelectItem value="4px">Small</SelectItem>
                <SelectItem value="8px">Medium</SelectItem>
                <SelectItem value="12px">Large</SelectItem>
                <SelectItem value="16px">Extra Large</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <Label>Font Family</Label>
            <Select value={fontFamily} onValueChange={setFontFamily}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Inter">Inter</SelectItem>
                <SelectItem value="Arial">Arial</SelectItem>
                <SelectItem value="Helvetica">Helvetica</SelectItem>
                <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                <SelectItem value="Georgia">Georgia</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <Label>Chat Height</Label>
            <Input
              type="text"
              value={chatHeight}
              onChange={(e) => setChatHeight(e.target.value)}
              placeholder="e.g., 600px or 80vh"
            />
          </div>

          <div className="space-y-4">
            <Label>Chat Width</Label>
            <Input
              type="text"
              value={chatWidth}
              onChange={(e) => setChatWidth(e.target.value)}
              placeholder="e.g., 100% or 400px"
            />
          </div>

          <Button
            onClick={() => updateChatbot()}
            disabled={isPending}
            className="w-full"
          >
            {isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  )
}